import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  FolderOpen, Upload, FileText, Shield, Scale, Flame, Briefcase,
  AlertTriangle, Clock, Check, Trash2, Download, Pencil, RefreshCw,
  Loader2, Search, X, ChevronRight, Users,
} from 'lucide-react';

const CATEGORY_META = {
  all:       { label: 'All',        icon: FolderOpen, color: 'text-foreground' },
  employee:  { label: 'Employee',   icon: Users,      color: 'text-blue-600' },
  insurance: { label: 'Insurance',  icon: Shield,     color: 'text-emerald-600' },
  license:   { label: 'Licenses',   icon: Scale,      color: 'text-purple-600' },
  safety:    { label: 'Safety',     icon: Flame,      color: 'text-orange-600' },
  legal:     { label: 'Legal',      icon: Briefcase,  color: 'text-slate-600' },
  other:     { label: 'Other',      icon: FileText,   color: 'text-gray-500' },
};

const STATUS_META = {
  valid:         { label: 'Valid',        className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  expiring:      { label: 'Expiring',     className: 'bg-amber-100 text-amber-800 border-amber-200' },
  expiring_soon: { label: 'Expiring Soon',className: 'bg-red-100 text-red-800 border-red-200' },
  expired:       { label: 'Expired',      className: 'bg-red-200 text-red-900 border-red-300' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.valid;
  return <Badge variant="outline" className={`${meta.className} border text-xs`}>{meta.label}</Badge>;
}

function daysUntilExpiry(expiryDate) {
  if (!expiryDate) return null;
  const diff = new Date(expiryDate) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Upload Dialog
function UploadDialog({ open, onOpenChange, api, documentTypes, onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  const selectedType = documentTypes.find(dt => dt.id === documentType);

  const handleUpload = async () => {
    if (!file || !title || !documentType) {
      toast.error('Please fill in title, type, and select a file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('document_type', documentType);
      formData.append('category', selectedType?.category || 'other');
      if (expiryDate) formData.append('expiry_date', new Date(expiryDate).toISOString());
      if (notes) formData.append('notes', notes);

      await api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setFile(null); setTitle(''); setDocumentType(''); setExpiryDate(''); setNotes('');
      onOpenChange(false);
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Auto-fill title from selected type
  useEffect(() => {
    if (selectedType && !title) setTitle(selectedType.name);
  }, [documentType]); // eslint-disable-line

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Upload Document</DialogTitle>
          <DialogDescription>Upload a PDF, image, or Word document</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Document Type *</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger data-testid="upload-doc-type"><SelectValue placeholder="Select type..." /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_META).filter(([k]) => k !== 'all').map(([cat, meta]) => {
                  const types = documentTypes.filter(dt => dt.category === cat);
                  if (types.length === 0) return null;
                  return (
                    <React.Fragment key={cat}>
                      <SelectItem value={`_header_${cat}`} disabled className="font-semibold text-xs uppercase tracking-wider">
                        {meta.label}
                      </SelectItem>
                      {types.map(dt => (
                        <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                      ))}
                    </React.Fragment>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Gas Safety Certificate 2026" data-testid="upload-doc-title" />
          </div>
          <div>
            <Label>File *</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={e => setFile(e.target.files[0])} data-testid="upload-doc-file" />
            {file && <p className="text-xs text-muted-foreground mt-1">{file.name} ({formatFileSize(file.size)})</p>}
          </div>
          <div>
            <Label>Expiry Date</Label>
            <Input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} data-testid="upload-doc-expiry" />
            {!selectedType?.requires_expiry && <p className="text-xs text-muted-foreground mt-1">Optional — add if this document expires</p>}
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..." rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleUpload} disabled={uploading || !file || !title || !documentType} data-testid="upload-doc-submit">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Renew Dialog
function RenewDialog({ open, onOpenChange, api, document: doc, onRenewed }) {
  const [file, setFile] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleRenew = async () => {
    if (!file) { toast.error('Select a file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (expiryDate) formData.append('expiry_date', new Date(expiryDate).toISOString());
      if (notes) formData.append('notes', notes);
      await api.post(`/documents/${doc.id}/renew`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document renewed');
      onOpenChange(false);
      onRenewed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Renewal failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5" /> Renew: {doc?.title}</DialogTitle>
          <DialogDescription>Upload the new version. The old one will be archived.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>New File *</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={e => setFile(e.target.files[0])} data-testid="renew-doc-file" />
          </div>
          <div>
            <Label>New Expiry Date</Label>
            <Input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} data-testid="renew-doc-expiry" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleRenew} disabled={uploading || !file} data-testid="renew-doc-submit">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Renew
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Dialog
function EditDialog({ open, onOpenChange, api, document: doc, documentTypes, onSaved }) {
  const [title, setTitle] = useState(doc?.title || '');
  const [documentType, setDocumentType] = useState(doc?.document_type || '');
  const [expiryDate, setExpiryDate] = useState(doc?.expiry_date ? doc.expiry_date.slice(0, 10) : '');
  const [notes, setNotes] = useState(doc?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/documents/${doc.id}`, {
        title,
        document_type: documentType,
        expiry_date: expiryDate ? new Date(expiryDate).toISOString() : null,
        notes,
      });
      toast.success('Document updated');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5" /> Edit Document</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Document Type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger data-testid="edit-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_META).filter(([k]) => k !== 'all').map(([cat, meta]) => {
                  const types = documentTypes.filter(dt => dt.category === cat);
                  if (types.length === 0) return null;
                  return (
                    <React.Fragment key={cat}>
                      <SelectItem value={`_header_${cat}`} disabled className="font-semibold text-xs uppercase tracking-wider">{meta.label}</SelectItem>
                      {types.map(dt => (<SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>))}
                    </React.Fragment>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} data-testid="edit-doc-title" />
          </div>
          <div>
            <Label>Expiry Date</Label>
            <div className="flex gap-2">
              <Input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} className="flex-1" data-testid="edit-doc-expiry" />
              {expiryDate && (
                <Button variant="ghost" size="sm" onClick={() => setExpiryDate('')} className="text-xs text-muted-foreground">Clear</Button>
              )}
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} data-testid="edit-doc-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !title} data-testid="edit-doc-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Document Row
function DocumentRow({ doc, api, documentTypes, onRefresh }) {
  const [downloading, setDownloading] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const days = daysUntilExpiry(doc.expiry_date);
  const catMeta = CATEGORY_META[doc.category] || CATEGORY_META.other;
  const CatIcon = catMeta.icon;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await api.post(`/documents/${doc.id}/archive`);
      toast.success('Document archived');
      onRefresh();
    } catch {
      toast.error('Archive failed');
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete "${doc.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success('Document deleted');
      onRefresh();
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="border rounded-lg hover:bg-muted/30 transition-colors" data-testid={`doc-row-${doc.id}`}>
        {/* Main row */}
        <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 ${catMeta.color}`}>
              <CatIcon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-sm truncate">{doc.title}</p>
                <StatusBadge status={doc.status} />
                {doc.is_critical && <Badge variant="outline" className="border-red-300 text-red-600 text-xs">Critical</Badge>}
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                <span>{doc.document_type_name || doc.document_type}</span>
                {doc.expiry_date && (
                  <span className={days !== null && days <= 30 ? 'text-red-600 font-medium' : days !== null && days <= 90 ? 'text-amber-600' : ''}>
                    {days !== null && days < 0
                      ? `Expired ${Math.abs(days)} days ago`
                      : days !== null
                        ? `Expires in ${days} days`
                        : ''}
                  </span>
                )}
                {doc.notes && <span className="hidden sm:inline truncate max-w-[200px]">{doc.notes}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-2">
            {(doc.status === 'expired' || doc.status === 'expiring_soon') && (
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setRenewOpen(true); }} className="text-xs" data-testid={`renew-${doc.id}`}>
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Renew
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setEditOpen(true); }} data-testid={`edit-${doc.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDownload(); }} disabled={downloading} data-testid={`download-${doc.id}`}>
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </Button>
            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </div>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="px-3 pb-3 pt-0 border-t mx-3 mb-3 mt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Category</p>
                <p>{CATEGORY_META[doc.category]?.label || doc.category}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Uploaded</p>
                <p>{doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-GB') : '—'}</p>
              </div>
              {doc.expiry_date && (
                <div>
                  <p className="text-xs text-muted-foreground">Expiry Date</p>
                  <p>{new Date(doc.expiry_date).toLocaleDateString('en-GB')}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">File</p>
                <p>{doc.original_filename || '—'} {doc.file_size ? `(${formatFileSize(doc.file_size)})` : ''}</p>
              </div>
              {doc.notes && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Notes</p>
                  <p className="whitespace-pre-wrap">{doc.notes}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t">
              <Button variant="outline" size="sm" onClick={handleArchive} disabled={archiving} data-testid={`archive-${doc.id}`}>
                {archiving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FolderOpen className="w-3.5 h-3.5 mr-1" />}
                Archive
              </Button>
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={handleDelete} disabled={deleting} data-testid={`delete-${doc.id}`}>
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                Delete
              </Button>
            </div>
          </div>
        )}
      </div>
      {renewOpen && <RenewDialog open={renewOpen} onOpenChange={setRenewOpen} api={api} document={doc} onRenewed={onRefresh} />}
      {editOpen && <EditDialog open={editOpen} onOpenChange={setEditOpen} api={api} document={doc} documentTypes={documentTypes} onSaved={onRefresh} />}
    </>
  );
}

// Expiring Soon Widget
function ExpiringSoonWidget({ documents }) {
  const expiring = documents.filter(d => d.status === 'expired' || d.status === 'expiring_soon' || d.status === 'expiring');
  if (expiring.length === 0) return null;

  const expired = expiring.filter(d => d.status === 'expired');
  const soon = expiring.filter(d => d.status === 'expiring_soon');
  const upcoming = expiring.filter(d => d.status === 'expiring');

  return (
    <Card className={expired.length > 0 ? 'border-red-300 bg-red-50/50' : soon.length > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className={`w-4 h-4 ${expired.length > 0 ? 'text-red-600' : 'text-amber-600'}`} />
          Attention Required
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {expired.length > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-red-700 font-medium">{expired.length} expired</span>
            <div className="flex gap-1 flex-wrap justify-end">
              {expired.slice(0, 3).map(d => (
                <Badge key={d.id} variant="outline" className="border-red-300 text-red-700 text-xs">{d.title}</Badge>
              ))}
              {expired.length > 3 && <Badge variant="outline" className="text-xs">+{expired.length - 3}</Badge>}
            </div>
          </div>
        )}
        {soon.length > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-amber-700 font-medium">{soon.length} expiring within 30 days</span>
            <div className="flex gap-1 flex-wrap justify-end">
              {soon.slice(0, 3).map(d => (
                <Badge key={d.id} variant="outline" className="border-amber-300 text-amber-700 text-xs">{d.title}</Badge>
              ))}
            </div>
          </div>
        )}
        {upcoming.length > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{upcoming.length} expiring within 90 days</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Main Page
export default function DocumentsPage() {
  const { api } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, typesRes] = await Promise.all([
        api.get('/documents'),
        api.get('/document-types'),
      ]);
      setDocuments(docsRes.data);
      setDocumentTypes(typesRes.data);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const filteredDocs = documents.filter(d => {
    if (activeCategory !== 'all' && d.category !== activeCategory) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        d.title?.toLowerCase().includes(term) ||
        d.document_type_name?.toLowerCase().includes(term) ||
        d.notes?.toLowerCase().includes(term) ||
        d.original_filename?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const categoryCounts = documents.reduce((acc, d) => {
    acc[d.category] = (acc[d.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
          <p className="text-muted-foreground text-sm">Certificates, licenses, insurance & compliance documents</p>
        </div>
        <Button onClick={() => setUploadOpen(true)} data-testid="upload-doc-btn">
          <Upload className="w-4 h-4 mr-2" /> Upload Document
        </Button>
      </div>

      {/* Expiring Widget */}
      <ExpiringSoonWidget documents={documents} />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search documents..."
          className="pl-9"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          data-testid="doc-search"
        />
        {searchTerm && (
          <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearchTerm('')}>
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="flex-wrap h-auto gap-1">
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const count = key === 'all' ? documents.length : (categoryCounts[key] || 0);
            return (
              <TabsTrigger key={key} value={key} className="gap-1.5" data-testid={`cat-tab-${key}`}>
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{meta.label}</span>
                {count > 0 && <Badge variant="secondary" className="text-xs px-1.5 py-0">{count}</Badge>}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value={activeCategory} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-muted-foreground">Loading documents...</span>
            </div>
          ) : filteredDocs.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FolderOpen className="w-12 h-12 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground text-sm">
                  {searchTerm ? 'No documents match your search' : 'No documents uploaded yet'}
                </p>
                {!searchTerm && (
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => setUploadOpen(true)}>
                    <Upload className="w-4 h-4 mr-1" /> Upload your first document
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredDocs.map(doc => (
                <DocumentRow key={doc.id} doc={doc} api={api} documentTypes={documentTypes} onRefresh={fetchDocuments} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        api={api}
        documentTypes={documentTypes}
        onUploaded={fetchDocuments}
      />
    </div>
  );
}
